<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ConfirmSyncInvoice',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    invoice: {
      type: Object,
      required: true,
    },
    formId: {
      type: String,
      required: false,
      default: function () {
        return uniqueID();
      },
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const labelAlign = ref('right');
  const payload = ref({
    id: props.invoice.id,
    print: true,
    print_copies: 1,
    target: 'remote',
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));

  const $data = computed(() => ({
    labelAlign: labelAlign.value,
    payload: payload.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'labelAlign' },
      { name: 'payload' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {},
    ux: {},
    dataRefs: {
      labelAlign,
      payload,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('ConfirmSyncInvoice.Title', 'Create invoice'),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.invoice,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice');
    }
  );
  watch(
    () => props.formId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('formId');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_6nd7gp7pp_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_4186377094414664_closeModal_nbvijklbc_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data.response.data,
      },
    };
  };
  const act_4186377094414664_request_zfoso5yqt_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'issue_invoice',
      data: payload.value,
    };
  };

  const act_7315092382398562_closeModal_6nd7gp7pp =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_6nd7gp7pp_getActionArgs,
    });
  const inst_evh_7315092382398562_close_7qjiiitjw =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_6nd7gp7pp,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_7qjiiitjw],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_u7imb9r17 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_p6910y5ai =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_u7imb9r17,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_p6910y5ai],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_jzemb3ypw =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_xz2qiqybw =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_jzemb3ypw,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_xz2qiqybw],
    event: 'close',
  });
  const act_4186377094414664_closeModal_nbvijklbc =
    new actionTypes.CloseModalAction({
      actionArgs: act_4186377094414664_closeModal_nbvijklbc_getActionArgs,
    });
  const inst_evh_4186377094414664_success_02ofyva67 =
    new actionTypes.EventHandlerImpl({
      action: act_4186377094414664_closeModal_nbvijklbc,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_4186377094414664_request_zfoso5yqt = new actionTypes.RequestAction({
    actionArgs: act_4186377094414664_request_zfoso5yqt_getActionArgs,
    displayName: 'submit',
    events: [inst_evh_4186377094414664_success_02ofyva67],
  });
  const inst_evh_4186377094414664_submit_pw972624j =
    new actionTypes.EventHandlerImpl({
      action: act_4186377094414664_request_zfoso5yqt,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_4186377094414664_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4186377094414664_submit_pw972624j],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_nmsmsvpop =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_ucmntf17r =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_nmsmsvpop,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_ucmntf17r],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_7qjiiitjw,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_p6910y5ai,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_xz2qiqybw,
    inst_evh_8564662037462133_close,
    inst_evh_4186377094414664_success_02ofyva67,
    inst_evh_4186377094414664_submit_pw972624j,
    submit: inst_evh_4186377094414664_submit_pw972624j,
    inst_evh_4186377094414664_submit,
    inst_evh_2434514588044777_clickPreventStop_ucmntf17r,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_7qjiiitjw = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_7qjiiitjw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_p6910y5ai = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_p6910y5ai.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_xz2qiqybw = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_xz2qiqybw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4186377094414664_success_02ofyva67 = async (event, scope) => {
    return await actions.inst_evh_4186377094414664_success_02ofyva67.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4186377094414664_submit_pw972624j = async (event, scope) => {
    return await actions.inst_evh_4186377094414664_submit_pw972624j.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4186377094414664_submit = async (event, scope) => {
    return await actions.inst_evh_4186377094414664_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_ucmntf17r = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_ucmntf17r.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ConfirmSyncInvoice"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpAlert variant="info" show>
        <p class="d-flex align-items-start gap-2 mb-0">
          <ItpIcon fa="info-circle"> </ItpIcon>
          <ItpText
            wrap
            :text="
              pgettext(
                'InvoiceSync',
                'After synchronization, the invoice can no longer be modified.'
              )
            "
          >
          </ItpText>
        </p>
      </ItpAlert>
      <ItpForm
        :id="formId"
        name="confirmSyncInvoice"
        ref="mainForm"
        @submit="evh_4186377094414664_submit($event, {})"
      >
        <ItpFormGroup
          name="print"
          :label="pgettext('InvoiceSync', 'Print')"
          :labelAlign="labelAlign"
          :label-cols-md="6"
          :content-cols-md="6"
        >
          <ItpFormCheckbox
            name="print"
            v-model="payload.print"
            :value="payload.print"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (payload.print =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            required
            size="md"
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
        <ItpFormGroup
          name="print_copies"
          :label="pgettext('InvoiceSync', 'Copies')"
          :labelAlign="labelAlign"
          :label-cols-md="6"
          :content-cols-md="6"
        >
          <ItpFormInput
            type="number"
            name="print_copies"
            v-model="payload.print_copies"
            :value="payload.print_copies"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (payload.print_copies =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            required
            :min="1"
            :disabled="!payload.print"
          >
          </ItpFormInput>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Create invoice')"
          :disabled="
            !forms.confirmSyncInvoice || !forms.confirmSyncInvoice.submittable
          "
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Cancel')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
